import BaseAction from "../base-action";
import ActionController from "../action-controller";
import {ActionTypeEnum} from "../../base/training-literals";

class BaseMove extends BaseAction {
    animateMovement;
    animateTime;
    position;
    quaternion;
    scale;
    pivot;

    constructor(props = {}) {
        super(props);
        let self = this;
        this.animateMovement = props.animateMovement || true;
        this.animateTime = props.animateTime || 2000;
        this.position = props.position || [0, 0, 0];
        this.quaternion = props.quaternion || [0, 0, 0, 1];
        this.scale = props.scale || [1, 1, 1];
        this.pivot = props.pivot || null;

        this.startModelSelection = false;
        this.cancelSelection = false;
        this.hideMenuItems = false;

        wv.communicator.addEventListener(text_startModelSelection, function (itemArr) {
            const actionId = itemArr[0];
            const entityNames = itemArr.slice(1);
            if (!self.shouldExecute(actionId)) return;
            if(entityNames.length > 0) {
                self.selectEntityForEditingMove(entityNames);
                MoveObjectActionController.moveObjectEditingAction = true;
            } else {
                MoveObjectActionController.resetStates();
                self.startSelectionForMove();
            }
        });

        wv.communicator.addEventListener(text_cancelModelSelection, function (itemArr) {
            const actionId = itemArr[0];
            if (!self.shouldExecute(actionId)) return;
            self.cancelModelSelectionCallback();
        });

        wv.communicator.addEventListener(text_performModelSelection, function (itemArr) {
            const actionId = itemArr[0];
            if (!self.shouldExecute(actionId)) return;

            const moveData = {
                position: itemArr[1].split(",").map(v => parseFloat(v)),
                quaternion: itemArr[2].split(",").map(v => parseFloat(v)),
                scale: itemArr[3].split(",").map(v => parseFloat(v))
            };
            const entityNames = itemArr.slice(4);

            MoveObjectActionController.moveObjectEditingAction = true;
            Promise.resolve(self.setObjectToPosition(entityNames, moveData)).then(() => {
                self.selectEntityForEditingMove(entityNames);
            });
        });

        wv.communicator.addEventListener(text_setToOrigin, function (itemArr) {
            const actionId = itemArr[0];
            if (!self.shouldExecute(actionId)) return;

            const entityNames = self.getSelectedEntityNamesForMove();
            if (!entityNames.length) {
                return;
            }

            const moveData = {
                position: [0, 0, 0],
                quaternion: [0, 0, 0, 1],
                scale: [1, 1, 1]
            };
            self.cancelModelSelectionCallback();
            MoveObjectActionController.moveObjectEditingAction = true;
            Promise.resolve(self.setObjectToPosition(entityNames, moveData)).then(() => {
                self.selectEntityForEditingMove(entityNames);
            });
        });

        wv.communicator.addEventListener(text_resetObjectPosition, function (itemArr) {
            const actionId = itemArr[0];
            if (!self.shouldExecute(actionId)) return;
            const entityNames = itemArr.slice(1);
            if (!entityNames.length) {
                return;
            }
            const moveData = {
                position: [0, 0, 0],
                quaternion: [0, 0, 0, 1],
                scale: [1, 1, 1]
            };
            self.setObjectToPosition(entityNames, moveData)
        });

        wv.communicator.addEventListener(text_getObjectMatrixForMoveObject, function (itemArr) {
            const actionId = itemArr[0];
            const unsuccessfulResponse = {
                actionId: self.actionId,
                success: false
            };

            if (!self.shouldExecute(actionId)) {
                return unsuccessfulResponse;
            }

            let errorMessage = '';
            if (!MoveObjectActionController.modelId) {
                errorMessage = "No object found to extract data. Please perform move and rotate operation on objects.";
            } else if (wv.effectController.modelCaught || wv.effectController.bodyCaught) {
                errorMessage = "Please complete current move and rotate operation and try again.";
            }

            if (errorMessage) {
                VRMessageController.addMessage({
                    text: errorMessage,
                    priority: VrMessagePriorityEnum.WARNING
                });

                return unsuccessfulResponse;
            }

            const {position, quaternion, scale, pivot} = self.getData();

            const payload = {
                success: true,
                modelId: MoveObjectActionController.modelId,
                modelName: MoveObjectActionController.modelName,
                volumeName: MoveObjectActionController.volumeName,
                volumeId: modelManager.getBranchIdFromVolumeName(MoveObjectActionController.volumeName),
                groupId: MoveObjectActionController.groupId,
                groupName: MoveObjectActionController.groupName,
                branchIdList: MoveObjectActionController.branchIdList ? MoveObjectActionController.branchIdList : [],
                position,
                quaternion,
                scale,
                pivot,
                actionId: actionId,
            };

            self.cancel();

            return payload;
        });
    }

    cancelModelSelectionCallback() {
        this.cancelSelection = true;
        this.cancel();
    }

    startSelectionForMove(applyToType) {
        this.cancelSelection = false;
        this.set2dElementsVisibility(false);
        clearAllFeaturesFromAllHandler();
        CatchVolumeFeatureController.onChangeMoveFeature(true);
        CatchVolumeFeatureController.onSelectApplyToType(applyToType);
    }

    set2dElementsVisibility(value) {
        this.hideMenuItems = !value;
        let _2dElements = getAll2DElements();
        _2dElements.forEach(element => {
            if (element.id === 'menuViewToolbar') {
                return;
            }
            element.style.visibility = value ? 'visible' : 'hidden';
        });

        if (this.hideMenuItems) {
            MoveObjectActionController.enableFeature();
            const visible2DMenus = BaseMove.getVisible2DMenus();
            menuViewToolbarController.showOnlyTheseButtons(visible2DMenus);
        } else {
            MoveObjectActionController.disableFeature();
            wv.show2DToolbarItems = true;
            document.dispatchEvent(new CustomEvent('show2DToolbar.enabled'));
        }

        mController.hideWholeMenu();

        JSTreeManager.hideAllOtherTreeViews();
        JSTreeManager.showModelTreeView('modelTreeViewSideNav', false);
    }

    static getVisible2DMenus() {
        const moveAndRotate = wv.menuViewToolbarData.toolbarContainer
            .find(container => container.id === 'modelToolsToolbar').children
            .find(modelTool => modelTool.id === 'MoveFeatureEnabled').children
            .map(feature => feature.id)
            .filter(menuId => !menuId.startsWith('Move'));

        const visible2DMenus = ['MoveWithPrecisionMenu', ...moveAndRotate];

        if (['MoveModel', 'MoveVolume'].includes(ActionController.getActiveAction()?.actionType)) {
            visible2DMenus.push('ConnectorMenu');
        }

        return visible2DMenus;
    }

    cancel() {
        updateCatchModelPosition(undefined, false);
        resetCatchAndMoveVolume();
        CatchVolumeFeatureController.onChangeMoveFeature(false);
        CatchVolumeFeatureController.onChangeMoveBodyPrecision(false);
        if (ConnectorController.anchor || ConnectorController.mover) {
            ConnectorController.clearAllSelection();
        }
        this.startModelSelection = false;
        if (!wv.isSceneModificationDisabled) {
            this.set2dElementsVisibility(true);
        }
        MoveObjectActionController.resetStates();
    }

    getMoveMatrix(moveData) {
        const position = new THREE.Vector3(...moveData.position);
        const quaternion = new THREE.Quaternion(...moveData.quaternion);
        const scale = new THREE.Vector3(...moveData.scale);

        return new THREE.Matrix4().compose(position, quaternion, scale);
    }

    getSelectedEntityNamesForMove() {
        if (this.actionType === ActionTypeEnum.MOVE_VOLUME) {
            return MoveObjectActionController.branchId ? [MoveObjectActionController.branchId] : [];
        }
        if (this.actionType === ActionTypeEnum.MOVE_GROUP) {
            if (MoveObjectActionController.groupId) {
                return [MoveObjectActionController.groupId];
            }
            return MoveObjectActionController.groupName ? [MoveObjectActionController.groupName] : [];
        }
        if (this.actionType === ActionTypeEnum.MOVE_BRANCH) {
            return Array.isArray(MoveObjectActionController.branchIdList) ? MoveObjectActionController.branchIdList : [];
        }
        if (this.actionType === ActionTypeEnum.MOVE_MODEL) {
            return MoveObjectActionController.modelId ? [MoveObjectActionController.modelId] : [];
        }
        return [];
    }

    setObjectToPosition(entityNames, moveData) {
        // To be overridden by subclasses
        console.warn("setObjectToPosition not implemented in", this.constructor.name);
    }
}

export default BaseMove;
